import React, { useState, useContext } from 'react';
import Panel from '../../../../components/Panel';
import FormInput from '../../../../components/FormInput';
import RoundedButton from '../../../../components/RoundedButton';
import Notification from '../../../../components/Notification';
import AuthContext from '../../../../contexts/AuthContext';
import './AccountSettings.scss';
import useAPI from '../../../../hooks/useAPI';

export default function AccountSettings () {
    const {
        user,
        onUpdateUser
    } = useContext(AuthContext);
    const api = useAPI();

    const [firstName, setFirstName] = useState(user.first_name || '');
    const [lastName, setLastName] = useState(user.last_name || '');
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordErrors, setPasswordErrors] = useState({});
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState('');

    const handleChangePassword = () => {
        const _errors = {};
        if (!currentPassword) _errors.current_password = 'Current password required.';
        if (!newPassword) {
            _errors.password = 'New password required.';
        } else if (newPassword.length < 8) {
            _errors.password = 'Password must be at least 8 characters.';
        } else if (!/[0-9]+/.test(newPassword)) {
            _errors.password = 'Password must contain at least 1 number.';
        }
        if (!confirmPassword) {
            _errors.confirm_password = 'Please confirm your new password.';
        } else if (confirmPassword !== newPassword) {
            _errors.confirm_password = 'Passwords do not match.';
        }

        setPasswordErrors(_errors);
        if (Object.keys(_errors).length) return;

        setChangingPassword(true);
        setPasswordSuccess('');
        api.json('POST', '/account/change-password', {
            current_password: currentPassword,
            password: newPassword,
        })
            .then((r) => r.json())
            .then((response) => {
                if (response.errors) {
                    setPasswordErrors(
                        Object.fromEntries(
                            Object.entries(response.errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])
                        )
                    );
                    return;
                }
                setPasswordSuccess('Password changed successfully.');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            })
            .catch(() => setPasswordErrors({ current_password: 'Unable to change password. Try again later.' }))
            .then(() => setChangingPassword(false));
    };

    const handleSave = () => {
        let _errors = {}

        if (!firstName) {
            _errors.first_name = 'First name required.';
        }

        if (!lastName) {
            _errors.last_name = 'Last name required.';
        }

        setErrors(_errors);
        if (Object.keys(_errors).length) {
            return;
        }

        setSaving(true);
        api.put('/account', {
            first_name: firstName,
            last_name: lastName
        }).then((r) => {
            return r.json();
        }).then((r) => {
            if (r.status === 'ok') {
                onUpdateUser({
                    ...user,
                    first_name: firstName,
                    last_name: lastName
                });
            } else {
                alert('Unable to save information. Try again later.');
            }
        }).catch((e) => alert(e))
            .then(() => setSaving(false));
    };

    return (
        <div className='account-settings'>
            <Panel
                title="Account Settings"
                headerStyle={{
                    borderBottomWidth: 1,
                    borderStyle: 'solid',
                    borderColor: '#E2E3E8',
                    paddingBottom: 20
                }}
                contentStyle={{
                    paddingTop: 20
                }}
            >
                <div className='account-settings--content'>
                    <div className='row'>
                        <div className='col-12 col-lg-6'>
                            <FormInput
                                label={'First Name'}
                                placeholder="Your First Name"
                                required
                                onChange={(e) => {
                                    setFirstName(e.target.value);
                                    setErrors({
                                        ...errors,
                                        first_name: ''
                                    });
                                }}
                                disabled={saving}
                                value={firstName}
                                error={errors.first_name}
                                maxLength="40"
                            />
                        </div>
                        <div className='col-12 col-lg-6'>
                            <FormInput
                                label={'Last Name'}
                                placeholder="Your Last Name"
                                required
                                onChange={(e) => {
                                    setLastName(e.target.value);
                                    setErrors({
                                        ...errors,
                                        last_name: ''
                                    });
                                }}
                                disabled={saving}
                                value={lastName}
                                error={errors.last_name}
                                maxLength="40"
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-12 col-lg-6'>
                            <FormInput
                                label={'Email Address'}
                                value={user.email || ''}
                                disabled
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-12'>
                            <RoundedButton
                                solid
                                onClick={handleSave}
                                busy={saving}
                            >Save Changes</RoundedButton>
                        </div>
                    </div>
                </div>
            </Panel>
            <Panel
                title="Change Password"
                headerStyle={{
                    borderBottomWidth: 1,
                    borderStyle: 'solid',
                    borderColor: '#E2E3E8',
                    paddingBottom: 20
                }}
                contentStyle={{
                    paddingTop: 20
                }}
            >
                <div className='account-settings--content'>
                    {passwordSuccess && (
                        <div className='row' style={{ marginBottom: 16 }}>
                            <div className='col-12'>
                                <Notification
                                    type="success"
                                    title="Success"
                                    timed={4000}
                                    onClose={() => setPasswordSuccess('')}
                                >
                                    {passwordSuccess}
                                </Notification>
                            </div>
                        </div>
                    )}
                    <div className='row'>
                        <div className='col-12 col-lg-6'>
                            <FormInput
                                label="Current Password"
                                type="password"
                                showToggle
                                maxLength="40"
                                value={currentPassword}
                                onChange={(e) => {
                                    setCurrentPassword(e.target.value);
                                    setPasswordErrors({ ...passwordErrors, current_password: '' });
                                }}
                                error={passwordErrors.current_password}
                                disabled={changingPassword}
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-12 col-lg-6'>
                            <FormInput
                                label="New Password"
                                type="password"
                                showToggle
                                maxLength="40"
                                value={newPassword}
                                onChange={(e) => {
                                    setNewPassword(e.target.value);
                                    setPasswordErrors({ ...passwordErrors, password: '' });
                                }}
                                error={passwordErrors.password}
                                disabled={changingPassword}
                            />
                        </div>
                        <div className='col-12 col-lg-6'>
                            <FormInput
                                label="Confirm New Password"
                                type="password"
                                showToggle
                                maxLength="40"
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    setPasswordErrors({ ...passwordErrors, confirm_password: '' });
                                }}
                                error={passwordErrors.confirm_password}
                                disabled={changingPassword}
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-12'>
                            <RoundedButton
                                solid
                                onClick={handleChangePassword}
                                busy={changingPassword}
                            >
                                Change Password
                            </RoundedButton>
                        </div>
                    </div>
                </div>
            </Panel>
        </div>
    );
};